import { createPerfilApiClient } from './perfilApiClient';
import { config } from '@/config';

/**
 * Cesión Temporal de Asiento Service
 *
 * Handles all HTTP calls for the temporary seat transfer flow.
 * Uses `createPerfilApiClient()` for authentication and header management.
 * All errors are propagated as-is to the composable for normalization.
 */

const BASE = abonadoId => `/f/perfil/abono/${abonadoId}/cesion-temporal-asiento`;

/**
 * Unwraps backend envelope: { data: {...} } → {...}
 * Falls back to the payload itself if not wrapped.
 */
function unwrap(payload) {
    if (payload && typeof payload === 'object' && 'data' in payload && !Array.isArray(payload.data)) {
        return payload.data;
    }
    return payload;
}

/**
 * Get the API key token from localStorage
 * @returns {string|null}
 */
function getApiKey() {
    return localStorage.getItem('token');
}

export default {
    /**
     * GET /api1/f/perfil/abono/{abonado_id}/cesion-temporal-asiento/eventos
     * @param {string|number} abonadoId
     * @returns {Promise<Array>} List of events available for transfer
     */
    async getEventos(abonadoId) {
        const client = createPerfilApiClient();
        const url = `${BASE(abonadoId)}/eventos`;
        const token = getApiKey();
        const response = await client.get(url, {
            params: { api_key: token }
        });
        const payload = response.data;
        if (Array.isArray(payload)) return payload;
        if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
            return payload.data;
        }
        return [];
    },

    /**
     * GET /api1/f/perfil/abono/{abonado_id}/cesion-temporal-asiento/evento/{evento_id}
     * Returns event info + cesion status for all sesiones.
     * 404 if event not available for this abonado.
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @returns {Promise<Object>} Event data with sesiones array
     */
    async getEvento(abonadoId, eventoId) {
        const client = createPerfilApiClient();
        const url = `${BASE(abonadoId)}/evento/${eventoId}`;
        const token = getApiKey();
        const response = await client.get(url, {
            params: { api_key: token }
        });
        return unwrap(response.data);
    },

    /**
     * GET /api1/f/perfil/abono/{abonado_id}/cesion-temporal-asiento/evento/{evento_id}/{sesion_id}/estado
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @returns {Promise<Object>} Current transfer status
     */
    async getEstado(abonadoId, eventoId, sesionId) {
        const client = createPerfilApiClient();
        const url = `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/estado`;
        const token = getApiKey();
        const response = await client.get(url, {
            params: { api_key: token }
        });
        return unwrap(response.data);
    },

    /**
     * POST /api1/f/perfil/abono/{abonado_id}/cesion-temporal-asiento/evento/{evento_id}/{sesion_id}/ceder
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @param {Object} payload Optional cesionario fields: nombre, apellidos, email, telefono, dni, user_id
     * @returns {Promise<Object>} Success response with updated estado
     */
    async ceder(abonadoId, eventoId, sesionId, payload = {}) {
        const client = createPerfilApiClient();
        const url = `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/ceder`;
        const token = getApiKey();
        const response = await client.post(url, payload, {
            params: { api_key: token }
        });
        return unwrap(response.data);
    },

    /**
     * DELETE /api1/f/perfil/abono/{abonado_id}/cesion-temporal-asiento/evento/{evento_id}/{sesion_id}/recuperar
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @returns {Promise<Object>} Success response with updated estado
     */
    async recuperar(abonadoId, eventoId, sesionId) {
        const client = createPerfilApiClient();
        const url = `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/recuperar`;
        const token = getApiKey();
        const response = await client.delete(url, {
            params: { api_key: token }
        });
        return unwrap(response.data);
    }
};
